import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import once from '@mailobj-browser/front/js/events/options/once.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import focus from '@mailobj-browser/front/js/events/types/focus.js'
import input from '@mailobj-browser/front/js/events/types/input.js'
import load from '@mailobj-browser/front/js/events/types/load.js'
import readystatechange from '@mailobj-browser/front/js/events/types/readyStateChange.js'
import resize from '@mailobj-browser/front/js/events/types/resize.js'
import selectionChange from '@mailobj-browser/front/js/events/types/selectionChange.js'
import closest from '@mailobj-browser/front/js/selectors/closest.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import rect from '#utilities/js/ea/rect.js'
import { elements, modifiers } from '../styles.js'
import { iframeOnInput } from './iframeOnInput.js'

export const rtes = new WeakMap()

const onLoad = object(listener, {
  type: load,
  passive,
  once,
  task: (
    iframe
  ) => {
    const { contentDocument } = iframe
    const { readyState } = contentDocument
    const rte = closest(`.${elements.rte}`, iframe)

    if (readyState === 'complete') {
      onReadyStateChange.task(contentDocument)
    } else {
      onReadyStateChange.listen(contentDocument)
    }

    rtes.set(contentDocument, rte)
    onResize.listen(contentDocument)
    onFocusIframe.listen(contentDocument)
    onInputIframe.listen(contentDocument)
    onSelectionChangeIframe.listen(contentDocument)

    queueMicrotask(async () => {
      await removed(rte)
      if (rtes.get(contentDocument) === rte) {
        rtes.delete(contentDocument)
      }
    })
  }
})

const onReadyStateChange = object(listener, {
  type: readystatechange,
  passive,
  task: (
    contentDocument
  ) => {
    const { readyState } = contentDocument

    if (readyState === 'complete') {
      iframeOnInput(contentDocument)
      onReadyStateChange.forget(contentDocument)
    }
  }
})

const onResize = object(listener, {
  type: resize,
  passive,
  task: (contentDocument) => {
    const rte = rtes.get(contentDocument)

    if (rte) {
      const actions = one(`.${elements.rte_actions}`, rte)
      const actionsVisibility = getComputedStyle(actions).getPropertyValue('--ea_rte_iframe_not_focused_actions_visibility')

      if (actionsVisibility === 'hidden') {
        rte.classList.add(modifiers.rte_iframe.focused)
      } else {
        rte.classList.remove(modifiers.rte_iframe.focused)
      }
    }
  }
})

const onFocusIframe = object(listener, {
  type: focus,
  passive,
  task: (contentDocument) => {
    const rte = rtes.get(contentDocument)

    if (rte) {
      const actions = one(`.${elements.rte_actions}`, rte)
      const { style } = rte
      const { height } = rect(actions)
      const actionsVisibility = getComputedStyle(actions).getPropertyValue('--ea_rte_iframe_not_focused_actions_visibility')

      if (actionsVisibility === 'hidden') {
        style.setProperty('--ea_rte_actions_height', `${height}px`)
        rte.classList.add(modifiers.rte_iframe.focused)
      }
    }
  }
})

const onCaretMove = object(listener, {
  passive,
  task: (
    contentDocument
  ) => {
    iframeOnInput(contentDocument)
  }
})

const onInputIframe = object(onCaretMove, {
  type: input
})

const onSelectionChangeIframe = object(onCaretMove, {
  type: selectionChange
})

export default rte => {
  const iframe = one(`.${elements.rte_iframe}`, rte)
  const { contentDocument } = iframe

  if (contentDocument) {
    return onLoad.task(iframe)
  }

  onLoad.listen(iframe)
}
